import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import OfferBadge from 'components/Common/OfferBadge'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import AspectRatio from 'components/utils/AspectRatio'
import { ImageParams } from 'components/Common/ResponsiveImage'
import PrimaryButtonPaletteProvider from 'contexts/PrimaryButtonPaletteProvider'
import PaletteProvider from 'contexts/PaletteProvider'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import HeroHotelOfferDetails from './HeroHotelOfferDetails'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { isMobile } from 'lib/deviceUtils'
import { connect } from 'react-redux'
import HeroOfferTileWrapper from './HeroOfferTileWrapper'

const MainImageSegment = styled.div`
  display: grid;
  position: relative;
`

const OfferOverlayContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(24)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const StyledOfferBadge = styled(OfferBadge)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
}

interface MappedProps {
  isMobile: boolean;
}

const imageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '19:9',
  mobileWidth: '75vw',
  tabletWidth: '55vw',
  desktopWidth: '55vw',
  largeDesktopWidth: '764px',
  quality: 'good',
}

function HeroHotelOfferTile(props: Props & MappedProps) {
  const {
    offer,
    eagerLoadFirstImage,
    isMobile,
  } = props

  return (
    <PrimaryButtonPaletteProvider palette="default">
      <HeroOfferTileWrapper>
        <MainImageSegment>
          <AspectRatio mobileAspectRatio="3:2" tabletAspectRatio="16:9" desktopAspectRatio="19:9" fillHeight>
            {isMobile &&
              <HeightFillingImage
                id={offer.images[0].id ?? ''}
                {...imageParams}
                loading={eagerLoadFirstImage ? 'eager' : undefined}
                withPlaceholder
              />
            }
            {!isMobile &&
              <ImageCarousel
                images={offer.images}
                imageParams={imageParams}
                eagerLoadFirstImage={eagerLoadFirstImage}
              />
            }
          </AspectRatio>
          <OfferOverlayContainer>
            <OfferPartnershipLabel offer={offer} variant="icon" />
          </OfferOverlayContainer>
          <TripGuard>
            <CSSBreakpoint max="tablet">
              <PaletteProvider type="default">
                <FloatingBookmarkContainer>
                  <BookmarkButton offer={offer} />
                </FloatingBookmarkContainer>
              </PaletteProvider>
            </CSSBreakpoint>
          </TripGuard>
          {offer.badge && config.BRAND === 'luxuryescapes' && <StyledOfferBadge badge={offer.badge} />}
        </MainImageSegment>
        <CSSBreakpoint max="tablet">
          <HeroHotelOfferDetails offer={offer} isDesktop={false} />
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <HeroHotelOfferDetails
            offer={offer}
            isDesktop
          />
        </CSSBreakpoint>
      </HeroOfferTileWrapper>
    </PrimaryButtonPaletteProvider>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isMobile: isMobile(state.config.userAgent.osName),
})

export default connect(mapStateToProps)(HeroHotelOfferTile)
